import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { X, Upload, Check } from 'lucide-react';
import { getCroppedImg } from "./lib/imageUtils.ts"; // make sure this exists!

const ProfilePhotoEditor = ({ onClose, onSave }) => {
    const [image, setImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fileInputRef = useRef(null);

    const handleSelectFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        console.log("File input triggered");
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.readAsDataURL(e.target.files[0]);
            reader.onload = () => {
                setImage(reader.result);
            };
        }
    };

    const onCropComplete = useCallback((_, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleSave = async () => {
        try {
            setIsLoading(true);
            if (!image || !croppedAreaPixels) return;

            const { blob, url } = await getCroppedImg(image, croppedAreaPixels);
            onSave(url, blob); // Send both!
        } catch (e) {
            console.error('Error cropping image:', e);
        } finally {
            setIsLoading(false);
        }
    };



    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 sm:p-6 md:p-8 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scaleIn">
                <div className="bg-gradient-to-r from-[#18727d] to-[#1a8d99] px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">Edit Profile Photo</h2>
                    <button onClick={onClose} className="text-white hover:text-teal-100 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6">
                    {!image ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="h-40 w-40 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center mb-6">
                                <Upload className="w-10 h-10 text-gray-400" />
                            </div>
                            <p className="text-gray-600 mb-6 text-center">
                                Upload an image to set as your profile photo
                            </p>
                            <button
                                onClick={handleSelectFile}
                                className="px-4 py-2 bg-[#18727d] text-white rounded-md hover:bg-[#145c65] transition-colors"
                            >
                                Select Image
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                className="hidden"
                            />
                        </div>
                    ) : (
                        <>
                            <div className="relative h-80 w-full mb-6">
                                <Cropper
                                    image={image}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    cropShape="round"
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                />
                            </div>

                            <div className="mb-6">
                                <label htmlFor="zoom" className="block text-sm font-medium text-gray-700 mb-1">
                                    Zoom: {zoom.toFixed(1)}x
                                </label>
                                <input
                                    id="zoom"
                                    type="range"
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    value={zoom}
                                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#18727d]"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleSelectFile}
                                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                >
                                    Change Image
                                </button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept="image/*"
                                    className="hidden"
                                />
                                <button
                                    onClick={handleSave}
                                    disabled={isLoading}
                                    className="flex items-center px-4 py-2 bg-[#18727d] text-white rounded-md hover:bg-[#145c65] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? 'Processing...' : (
                                        <>
                                            <Check className="w-4 h-4 mr-2" />
                                            Save Profile Photo
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfilePhotoEditor;
